import { Injectable, PLATFORM_ID, inject } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import { Firestore, doc, getDoc } from '@angular/fire/firestore';

interface TelegramConfig {
  telegramBotToken: string;
  telegramChatId: string;
}

@Injectable({ providedIn: 'root' })
export class TelegramService {

  private firestore = inject(Firestore);
  private http = inject(HttpClient);
  private platformId = inject(PLATFORM_ID);

  /* config lives in Firestore: classify/telegram
     { telegramBotToken: string, telegramChatId: string } */
  private config: TelegramConfig | null = null;
  private configPromise: Promise<TelegramConfig | null> | null = null;

  private loadConfig(): Promise<TelegramConfig | null> {

    if (this.config) return Promise.resolve(this.config);

    if (!this.configPromise) {
      this.configPromise = getDoc(doc(this.firestore, 'classify', 'telegram'))
        .then(snap => {

          const token = snap.exists() ? snap.data()?.['telegramBotToken'] : null;
          const chatId = snap.exists() ? snap.data()?.['telegramChatId'] : null;

          if (token && chatId) {
            this.config = { telegramBotToken: token, telegramChatId: chatId };
            return this.config;
          }

          // doc missing/incomplete — clear the cache so the next send retries
          this.configPromise = null;
          console.error('Telegram config missing in classify/telegram');
          return null;
        })
        .catch(err => {
          // allow a retry on the next send instead of caching the failure
          this.configPromise = null;
          console.error('Failed to load telegram config', err);
          return null;
        });
    }

    return this.configPromise;
  }

  async sendMessage(message: string): Promise<void> {

    if (!isPlatformBrowser(this.platformId)) return;

    const cfg = await this.loadConfig();
    if (!cfg) return;

    const url = `https://api.telegram.org/bot${cfg.telegramBotToken}/sendMessage`;

    this.http.post(url, {
      chat_id: cfg.telegramChatId,
      text: message,
      parse_mode: 'HTML'
    }).subscribe({
      error: err => console.error('Telegram send failed', err)
    });
  }
}
